import { trackArError, trackEvent } from "../analytics/analytics";
import { appConfig } from "../config";
import { SessionLifecycle } from "../lifecycle/session-lifecycle";
import {
  checkCompatibility,
  compatibilityFailureToArErrorCode,
  mapCameraError,
} from "../platform/compatibility";
import { detectWebView } from "../platform/webview-detection";
import { appState } from "../state/app-state";
import type { AppStatus, ArErrorCode } from "../types";
import { ArEngineLoadError, loadArEngine } from "./ar-loader";
import { createArScene, TargetLoadError } from "./ar-scene";

const LOAD_ENGINE_DURING_INTRO = true;

export interface ArControllerOptions {
  arContainer: HTMLElement;
  setLoading(visible: boolean, message?: string): void;
  setCtaVisible(visible: boolean): void;
  onNeedsUserRestart(): void;
}

export class ArController {
  private lifecycle?: SessionLifecycle;
  private startPromise?: Promise<void>;
  private preloadPromise?: Promise<void>;

  constructor(private readonly options: ArControllerOptions) {}

  prepare(): Promise<void> | undefined {
    if (LOAD_ENGINE_DURING_INTRO) {
      this.preloadPromise = loadArEngine().catch((error: unknown) => {
        this.preloadPromise = undefined;
        throw error;
      });
      return this.preloadPromise;
    }

    return undefined;
  }

  async start(): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.startInternal();

    try {
      await this.startPromise;
    } finally {
      this.startPromise = undefined;
    }
  }

  async stop(): Promise<void> {
    await this.lifecycle?.destroy();
    this.lifecycle = undefined;
    this.options.arContainer.replaceChildren();
    this.options.setCtaVisible(false);
  }

  async retry(): Promise<void> {
    await this.stop();
    appState.reset();
  }

  async openWhatsApp(): Promise<void> {
    trackEvent("cta_clicked");
    this.options.setCtaVisible(false);
    await this.lifecycle?.release("manual");
    this.lifecycle = undefined;
    window.location.assign(appConfig.whatsappUrl);
  }

  private async startInternal(): Promise<void> {
    trackEvent("ar_start_clicked");
    this.options.setCtaVisible(false);
    this.options.setLoading(true, "A preparar experiência…");

    const webview = detectWebView();

    if (webview.isWebView) {
      appState.transition("internal-browser");
      trackEvent("webview_blocked", { name: webview.name, reason: webview.reason });
      this.options.setLoading(false);
      return;
    }

    const compatibility = checkCompatibility();

    if (!compatibility.ok && compatibility.reason) {
      const code = compatibilityFailureToArErrorCode(compatibility.reason);
      appState.transition("incompatible", code);
      trackArError(code, { phase: "compatibility", reason: compatibility.reason });
      this.options.setLoading(false);
      return;
    }

    appState.transition("requesting-permission");

    try {
      await this.loadEngine();
      const session = await createArScene({
        container: this.options.arContainer,
        onTargetFound: () => this.handleTargetFound(),
        onTargetLost: () => this.handleTargetLost(),
        onCtaClick: () => {
          void this.openWhatsApp();
        },
      });

      this.lifecycle = new SessionLifecycle(() => session, {
        cleanupRoot: this.options.arContainer,
        onRestartRequired: () => this.handleRestartRequired(),
        onReleased: (reason) => {
          if (reason !== "manual") {
            this.lifecycle = undefined;
          }
        },
      });

      await this.lifecycle.start();
      trackEvent("camera_permission_granted");
      trackEvent("camera_started");
      appState.transition("camera-active");
    } catch (error) {
      await this.stop();
      this.handleStartError(error);
    } finally {
      this.options.setLoading(false);
    }
  }

  private async loadEngine(): Promise<void> {
    if (this.preloadPromise) {
      await this.preloadPromise;
      return;
    }

    await loadArEngine();
  }

  private handleTargetFound(): void {
    appState.transition("target-detected");
    this.options.setCtaVisible(true);
    trackEvent("target_detected");
  }

  private handleTargetLost(): void {
    appState.transition("target-lost");
    this.options.setCtaVisible(false);
    trackEvent("target_lost");
  }

  private handleRestartRequired(): void {
    this.options.setCtaVisible(false);
    appState.transition("camera-active");
    this.options.onNeedsUserRestart();
  }

  private handleStartError(error: unknown): void {
    if (error instanceof TargetLoadError) {
      appState.transition("target-missing", "TARGET_LOAD_FAILED");
      trackArError("TARGET_LOAD_FAILED", { phase: "target", message: error.message });
      return;
    }

    if (error instanceof ArEngineLoadError) {
      appState.transition("technical-error", "ENGINE_LOAD_FAILED");
      trackArError("ENGINE_LOAD_FAILED", { phase: "engine", message: error.message });
      return;
    }

    const cameraCode = mapCameraError(error);
    const suspiciousWebview = detectWebView();

    if (suspiciousWebview.isWebView && (cameraCode === "PERMISSION_DENIED" || cameraCode === "CAMERA_UNAVAILABLE")) {
      appState.transition("internal-browser");
      trackEvent("webview_blocked", {
        name: suspiciousWebview.name,
        reason: "camera-failed-in-webview",
      });
      return;
    }

    appState.transition(statusForCameraCode(cameraCode), cameraCode);
    if (cameraCode === "PERMISSION_DENIED") {
      trackEvent("camera_permission_denied");
    }
    trackArError(cameraCode, { phase: "camera" });
  }
}

function statusForCameraCode(code: ArErrorCode): AppStatus {
  if (code === "PERMISSION_DENIED") {
    return "permission-denied";
  }

  if (code === "CAMERA_UNAVAILABLE") {
    return "incompatible";
  }

  return "technical-error";
}
